import React, { useState } from "react";
import { toast } from "sonner";
import { Plus, Trash, ArrowCounterClockwise, Copy, WarningCircle, FloppyDisk, CopySimple } from "@phosphor-icons/react";
import { inputCls, PrimaryButton, GhostButton } from "@/components/admin/parts";
import { adminApi, getErrorMessage } from "@/lib/api";
import { ApplyDialog } from "@/components/admin/model-specs/ApplyDialog";

const toText = (groups) => (groups || []).map((g) => ({ name: g.name, text: g.items.join("\n") }));
const toGroups = (rows) => rows.map((g) => ({ name: g.name, items: g.text.split("\n") }));
const BLANK_ROW = { label: "", value: "", kind: "weight", guide: false };

function SectionHead({ title, edited, isCurrent, onReset, onCopy }) {
  return (
    <div className="flex items-center gap-3 mb-3">
      <h4 className="font-heading text-xs font-bold uppercase tracking-[0.2em] text-[#73bf44]">{title}</h4>
      {isCurrent && (edited ? (
        <button onClick={onReset} data-testid={`ms-reset-${title.split(" ")[0].toLowerCase()}`} className="inline-flex items-center gap-1 text-xs text-amber-300 hover:text-amber-200">
          <ArrowCounterClockwise size={12} /> Edited · reset to website
        </button>
      ) : <span className="text-xs text-neutral-600">From the website</span>)}
      {!isCurrent && (
        <button onClick={onCopy} data-testid={`ms-copy-${title.split(" ")[0].toLowerCase()}`} className="inline-flex items-center gap-1 text-xs text-neutral-400 hover:text-white">
          <Copy size={12} /> Copy from website (MY26)
        </button>
      )}
    </div>
  );
}

function KeySpecRow({ row, web, onChange, onRemove }) {
  const set = (k, v) => onChange({ ...row, [k]: v });
  return (
    <div className="grid grid-cols-[1.3fr_1fr_120px_80px_36px] gap-2 items-start" data-testid="ms-keyspec-row">
      <input value={row.label} onChange={(e) => set("label", e.target.value)} placeholder="e.g. Tare" className={inputCls} data-testid="ms-keyspec-label" />
      <div>
        <input value={row.value} onChange={(e) => set("value", e.target.value)} placeholder="2300 or 2,300 kg" className={inputCls} data-testid="ms-keyspec-value" />
        {web && web.value !== row.value && <div className="text-[0.65rem] text-neutral-600 mt-1">Website: {web.value}</div>}
      </div>
      <select value={row.kind === "weight" ? "weight" : "length"} onChange={(e) => set("kind", e.target.value)} className={inputCls} data-testid="ms-keyspec-kind">
        <option value="length">Dimension</option>
        <option value="weight">Weight</option>
      </select>
      <label className="flex items-center gap-1.5 text-xs text-neutral-400 pt-2.5" title="Show with an asterisk (guide only)">
        <input type="checkbox" checked={!!row.guide} onChange={(e) => set("guide", e.target.checked)} className="accent-[#73bf44]" /> Guide *
      </label>
      <button onClick={onRemove} className="p-2 rounded-lg text-neutral-500 hover:text-red-400 hover:bg-red-500/10"><Trash size={15} /></button>
    </div>
  );
}

function KeySpecsEditor({ rows, setRows, webSpecs }) {
  const update = (i, r) => setRows(rows.map((x, j) => (j === i ? r : x)));
  return (
    <div className="space-y-2" data-testid="ms-keyspecs">
      {rows.length === 0 && <p className="text-sm text-neutral-600">No measurements yet.</p>}
      {rows.map((r, i) => (
        <KeySpecRow key={i} row={r} web={webSpecs.find((w) => w.label === r.label)} onChange={(x) => update(i, x)} onRemove={() => setRows(rows.filter((_, j) => j !== i))} />
      ))}
      <button onClick={() => setRows([...rows, { ...BLANK_ROW }])} data-testid="ms-keyspec-add" className="inline-flex items-center gap-1.5 text-sm text-[#73bf44] hover:text-[#5fa338] pt-1"><Plus size={14} /> Add row</button>
    </div>
  );
}

function GroupsEditor({ rows, setRows }) {
  const update = (i, k, v) => setRows(rows.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  return (
    <div className="space-y-3" data-testid="ms-groups">
      {rows.length === 0 && <p className="text-sm text-neutral-600">No standard features yet. Customers won't see a Standard Features tab for this generation.</p>}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
        {rows.map((g, i) => (
          <div key={i} className="bg-black/30 border border-neutral-800 rounded-xl p-3 space-y-2" data-testid="ms-group">
            <div className="flex gap-2">
              <input value={g.name} onChange={(e) => update(i, "name", e.target.value)} placeholder="Group name, e.g. Electrical" className={inputCls} data-testid="ms-group-name" />
              <button onClick={() => setRows(rows.filter((_, j) => j !== i))} className="p-2 rounded-lg text-neutral-500 hover:text-red-400 hover:bg-red-500/10"><Trash size={15} /></button>
            </div>
            <textarea value={g.text} onChange={(e) => update(i, "text", e.target.value)} rows={6} placeholder="One feature per line" className={`${inputCls} resize-y leading-relaxed`} data-testid="ms-group-items" />
          </div>
        ))}
      </div>
      <button onClick={() => setRows([...rows, { name: "", text: "" }])} data-testid="ms-group-add" className="inline-flex items-center gap-1.5 text-sm text-[#73bf44] hover:text-[#5fa338]"><Plus size={14} /> Add group</button>
    </div>
  );
}

function StatusNote({ row }) {
  if (row.is_current) return null;
  const v = row.version;
  if (v?.key_specs && v?.groups) return null;
  const parts = [!v?.key_specs && "the MY26 measurements with a \"may differ\" note", !v?.groups && "no Standard Features tab"].filter(Boolean);
  return (
    <div className="flex gap-2 items-start rounded-lg border border-amber-500/30 bg-amber-500/[0.06] px-3 py-2.5 text-xs text-amber-200 mb-5" data-testid="ms-status-note">
      <WarningCircle size={15} weight="fill" className="shrink-0 mt-px" />
      Until this is filled in, Generation {row.generation} customers see {parts.join(" and ")}.
    </div>
  );
}

export function VersionEditor({ spec, row, onSaved, allSpecs }) {
  const v = row.version || {};
  const cur = row.is_current;
  const [ks, setKs] = useState(v.key_specs || (cur ? spec.key_specs : []));
  const [ksEdited, setKsEdited] = useState(!!v.key_specs);
  const [groups, setGroups] = useState(toText(v.groups || (cur ? spec.groups : [])));
  const [grEdited, setGrEdited] = useState(!!v.groups);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [applyOpen, setApplyOpen] = useState(false);
  const editKs = (r) => { setKs(r); setKsEdited(true); setDirty(true); };
  const editGroups = (r) => { setGroups(r); setGrEdited(true); setDirty(true); };
  const canApply = !dirty && (cur ? spec.groups?.length || v.groups : v.groups);

  const save = async () => {
    setSaving(true);
    try {
      await adminApi.put(`/admin/model-specs/${spec.id}/generations/${row.generation}`, {
        key_specs: cur && !ksEdited ? null : ks,
        groups: cur && !grEdited ? null : toGroups(groups),
      });
      toast.success(`Generation ${row.generation || ""} specs saved`.replace("  ", " "));
      onSaved();
    } catch (e) { toast.error(getErrorMessage(e)); }
    finally { setSaving(false); }
  };
  const remove = async () => {
    if (!window.confirm(`Delete the Generation ${row.generation} specs for ${spec.name}?`)) return;
    try { await adminApi.delete(`/admin/model-specs/${spec.id}/generations/${row.generation}`); toast.success("Deleted"); onSaved(); }
    catch (e) { toast.error(getErrorMessage(e)); }
  };

  return (
    <div data-testid={`ms-version-${row.generation}`}>
      <StatusNote row={row} />
      <SectionHead title="Measurements & Weights" isCurrent={cur} edited={ksEdited} onReset={() => { setKs(spec.key_specs); setKsEdited(false); setDirty(true); }} onCopy={() => editKs(spec.key_specs)} />
      <KeySpecsEditor rows={ks} setRows={editKs} webSpecs={cur ? spec.key_specs : []} />
      <div className="h-px bg-neutral-800 my-6" />
      <SectionHead title="Standard Features" isCurrent={cur} edited={grEdited} onReset={() => { setGroups(toText(spec.groups)); setGrEdited(false); setDirty(true); }} onCopy={() => editGroups(toText(spec.groups))} />
      <GroupsEditor rows={groups} setRows={editGroups} />
      <div className="flex items-center justify-end gap-2 mt-6 pt-4 border-t border-neutral-800">
        {!cur && row.version && <GhostButton onClick={remove} data-testid="ms-version-delete" className="mr-auto !border-red-500/40 !text-red-400"><Trash size={15} /> Delete Gen {row.generation} specs</GhostButton>}
        <GhostButton onClick={() => setApplyOpen(true)} disabled={!canApply} title={dirty ? "Save your changes first" : ""} data-testid="ms-apply-open" className="disabled:opacity-40 disabled:cursor-not-allowed">
          <CopySimple size={15} /> Apply features to other models
        </GhostButton>
        <PrimaryButton onClick={save} disabled={saving} data-testid="ms-version-save"><FloppyDisk size={16} /> {saving ? "Saving…" : "Save"}</PrimaryButton>
      </div>
      {dirty && <p className="text-xs text-neutral-500 text-right mt-2">Save your changes before applying them to other models.</p>}
      <ApplyDialog open={applyOpen} onClose={() => setApplyOpen(false)} spec={spec} gen={row.generation} allSpecs={allSpecs} onApplied={onSaved} />
    </div>
  );
}
